import { useEffect, useState, type ReactNode } from "react";

/** Number input that lets you clear/retype freely and only commits valid, clamped numbers. */
export function Num(props: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  title?: string;
  "aria-label"?: string;
}) {
  const { value, onChange, min = -999, max = 999_999, step = 1 } = props;
  const [text, setText] = useState(String(value));
  useEffect(() => {
    if (Number(text) !== value) setText(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      type="number"
      className={props.className}
      title={props.title}
      aria-label={props["aria-label"]}
      min={min}
      max={max}
      step={step}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        if (e.target.value === "" || e.target.value === "-") return;
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, step < 1 ? n : Math.round(n))));
      }}
      onBlur={() => setText(String(value))}
    />
  );
}

/** Optional number: empty = null (e.g. "auto" attack bonus, no limited uses). */
export function OptNum(props: { value: number | null; onChange: (v: number | null) => void; min?: number; max?: number; placeholder?: string; title?: string }) {
  const { value, onChange, min = -99, max = 99 } = props;
  const [text, setText] = useState(value === null ? "" : String(value));
  useEffect(() => {
    if ((text === "" ? null : Number(text)) !== value) setText(value === null ? "" : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      type="number"
      min={min}
      max={max}
      title={props.title}
      placeholder={props.placeholder}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        if (e.target.value === "") return onChange(null);
        const n = Math.round(Number(e.target.value));
        if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
      }}
    />
  );
}

/** Labelled field (label above control). */
export function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`field ${className ?? ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

/** Clickable pips: filled = used. Clicking a filled pip frees it (and the ones after), an empty one uses up to it. */
export function Pips({ max, used, onChange, disabled, title }: { max: number; used: number; onChange: (used: number) => void; disabled?: boolean; title?: string }) {
  if (max <= 0) return null;
  return (
    <span className="use-pips" title={title ?? `${max - used} of ${max} left`}>
      {Array.from({ length: max }, (_, i) => (
        <button
          key={i}
          type="button"
          disabled={disabled}
          className={`use-pip ${i < used ? "used" : ""}`}
          aria-label={i < used ? "Used: click to restore" : "Available: click to use"}
          onClick={() => onChange(i < used ? i : i + 1)}
        />
      ))}
    </span>
  );
}

/** Replace the item with matching id in a list. */
export function patchItem<T extends { id: string }>(list: T[], id: string, patch: Partial<T>): T[] {
  return list.map((x) => (x.id === id ? { ...x, ...patch } : x));
}
